// QUOTE LINES PAGINATION (quote-lines-pagination, 2026-10-06) -- the PURE logic.
//
// A page is a WINDOW onto the rows, nothing more. The rows, their line numbers,
// the totals, the save payload and the PDF never see a page: they are computed
// from the whole list elsewhere. This file only answers "which slice is on
// screen", "which page holds row N" and "which other rows still need a look".
//
// Row positions here are indexes into the list the table shows (its filtered
// view), 0-based. Line NUMBERS are a row's own `lineNo` and are not touched.

/** The page sizes on offer. 0 means "All": one page holding every row. */
export const PAGE_SIZES = [25, 50, 100, 0] as const;
export type PageSize = (typeof PAGE_SIZES)[number];
export const DEFAULT_PAGE_SIZE: PageSize = 50;
/** A list this long or shorter shows no pager at all. */
export const PAGER_THRESHOLD = 25;
export const PAGE_SIZE_STORAGE_KEY = "anixify.linePageSize";

export function isPageSize(value: unknown): value is PageSize {
  return PAGE_SIZES.some((s) => s === value);
}

export function pageSizeLabel(size: PageSize): string {
  return size === 0 ? "All" : String(size);
}

/** Whether a list of `total` rows is paged (and shows a pager). */
export function isPaged(total: number): boolean {
  return total > PAGER_THRESHOLD;
}

/** Rows per page; "All" is every row (at least 1, so division is safe). */
export function effectiveSize(size: PageSize, total: number): number {
  return size === 0 ? Math.max(1, total) : size;
}

export function pageCount(total: number, size: PageSize): number {
  if (total <= 0) return 1;
  return Math.max(1, Math.ceil(total / effectiveSize(size, total)));
}

/** A page index forced into [0, pageCount - 1]. */
export function clampPage(page: number, total: number, size: PageSize): number {
  const last = pageCount(total, size) - 1;
  if (!Number.isFinite(page)) return 0;
  return Math.max(0, Math.min(last, Math.trunc(page)));
}

/** The half-open row range [start, end) a page shows. Unpaged lists show all. */
export function pageRange(page: number, total: number, size: PageSize): { start: number; end: number } {
  if (!isPaged(total)) return { start: 0, end: Math.max(0, total) };
  const per = effectiveSize(size, total);
  const p = clampPage(page, total, size);
  const start = p * per;
  return { start, end: Math.min(total, start + per) };
}

/** The page holding row `rowIdx` (clamped into the list). */
export function pageOfIndex(rowIdx: number, total: number, size: PageSize): number {
  if (!isPaged(total) || total <= 0) return 0;
  const idx = Math.max(0, Math.min(total - 1, Math.trunc(rowIdx)));
  return Math.floor(idx / effectiveSize(size, total));
}

/** "Lines 51–100 of 182" built from the rows' own line numbers' POSITIONS. */
export function rangeLabel(page: number, total: number, size: PageSize, noun = "Lines"): string {
  if (total <= 0) return `No ${noun.toLowerCase()}`;
  const { start, end } = pageRange(page, total, size);
  return `${noun} ${start + 1}–${end} of ${total}`;
}

/** Page buttons: always the first and last, a window around the current page,
 *  and null for each gap ("…"). */
export function pageNumbers(page: number, count: number, around = 1): Array<number | null> {
  const out: Array<number | null> = [];
  const keep = new Set<number>([0, count - 1]);
  for (let p = page - around; p <= page + around; p += 1) if (p >= 0 && p < count) keep.add(p);
  const sorted = [...keep].sort((a, b) => a - b);
  sorted.forEach((p, i) => {
    if (i > 0) {
      const gap = p - sorted[i - 1] - 1;
      // A gap of one page is shown as that page: "…" would hide one button.
      if (gap === 1) out.push(sorted[i - 1] + 1);
      else if (gap > 1) out.push(null);
    }
    out.push(p);
  });
  return out;
}

/** Row positions (in the shown list) that need attention. */
export function attentionIndexes<T>(rows: readonly T[], needs: (row: T) => boolean): number[] {
  const out: number[] = [];
  rows.forEach((r, i) => { if (needs(r)) out.push(i); });
  return out;
}

/** The attention rows that are NOT on the visible range [start, end). */
export function otherPageAttention(indexes: readonly number[], start: number, end: number): number[] {
  return indexes.filter((i) => i < start || i >= end);
}

/** The next attention row after the visible range, wrapping to the first one
 *  before it. null when none lies off the page. */
export function nextAttentionIndex(indexes: readonly number[], start: number, end: number): number | null {
  const others = otherPageAttention(indexes, start, end);
  if (others.length === 0) return null;
  const after = others.find((i) => i >= end);
  return after !== undefined ? after : others[0];
}

// ---- the remembered page size (per browser) ----------------------------------

export function readStoredPageSize(): PageSize {
  try {
    const raw = window.localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
    if (raw === null) return DEFAULT_PAGE_SIZE;
    const n = Number(raw);
    return isPageSize(n) ? n : DEFAULT_PAGE_SIZE;
  } catch {
    return DEFAULT_PAGE_SIZE;
  }
}

export function storePageSize(size: PageSize): void {
  try {
    window.localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(size));
  } catch {
    /* private window / blocked storage: the choice just is not remembered */
  }
}
